import React, {useEffect, useState, useCallback} from 'react'
import { useHistory } from 'react-router';

import {
    Page,
    Container,
    ReverseSVG
} from "./AvailableJobsPage.styles";

import * as ls from 'local-storage'
import {exitReact, formatDate} from "../../../utils";
import Dropdown from "./components/Dropdown/Dropdown";
import Shifts from "./components/Shifts/Shifts";
import {PageDataProps} from "../../../App";
import { HygienistProps, JobProps } from "utils/types";
import Api from "../../../api";
import {usePersistedState} from "../../../hooks";
import {entities} from "../../../reduxStore";
import {
    distanceOptionsCA,
    distanceOptionsUS, filterByDistance, filterByRate,
    FiltersProps,
    rateOptsAst, rateOptsHyg, sorting, sortOptions,
    useAvailableJobs
} from "./AvailableJobs.utils";
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import {TopBarRedesigned} from "../../../components/TopBarRedesigned";

// @ts-ignore
const TempStars = window.TempStars


interface AvailableJobsProps {
    hygienist?: HygienistProps,
    pageData?: PageDataProps,
}

export default function AvailableJobsPage({}: AvailableJobsProps) {
    const [hygienist, setHygienist] = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    });

    const history = useHistory();
    const filters = ls.get<FiltersProps>('filters')
    const {date, withDate: withDateFromStorage} = history.location.state as { date: string, withDate?: boolean }
    const [withDate]= useState<boolean>(!!withDateFromStorage || !!filters?.withDate)

    const [updatingJobs, setUpdatingJobs] = useState<boolean>(true)
    const [availableJobs, hidedJobs, errorGetJos, loadingJobs] = useAvailableJobs(hygienist.type || 0, withDate ? date : undefined);
    const [filteredJobs, setFilteredJobs] = useState<JobProps[]>(availableJobs)
    const [favourite, setFavourite] = useState<boolean>(filters?.favourite || false);
    const [selectedSort, setSelectedSort] = useState<number>(filters?.selectedSort || 0);
    const [distanceIdx, setDistance] = useState<number>(filters?.distanceIdx || 0);
    const [rateIdx, setRate] = useState<number>(filters?.rateIdx || 0);
    const [options, setOptions] = useState<any>()
    const [refresh, setRefresh] = useState<boolean>(true)
    const [type, setType] = useState<'rate' | 'distance' | 'sort' | 'filters' | null>(null)
    const distanceOpts = hygienist.country ? distanceOptionsUS : distanceOptionsCA
    ls.remove('filters')
    const [exitReactState, setExitReactState] = useState<boolean>(false);
    const [updatingShifts, setUpdatingShifts] = useState<boolean>(false);
    const rateOpts = hygienist.type ? rateOptsAst : rateOptsHyg;

    const goToPage = useCallback((id: number, filter: any, isFavourite: boolean) => {
        const currentUser = TempStars.User.getCurrentUser();
        if(!currentUser?.hygienist?.Verified){
            TempStars.Verification.VerificationDescription(setExitReactState)
        } else {
            if (hygienist && !hygienist.Verified) {
                setHygienist({
                    ...hygienist,
                    Verified: currentUser.hygienist.Verified
                });
            }
            ls.set('filters', {
                distanceIdx,
                rateIdx,
                selectedSort,
                favourite,
                date: withDate ? date : undefined,
                withDate: withDate
            })
            history.push('availableJob', { jobId: id, filter, isFavourite })
        }
    }, [hygienist, setHygienist, distanceIdx, rateIdx, selectedSort, favourite, withDate, date, history])
    
    useEffect(() => {
        if(availableJobs) {
            setUpdatingJobs(true)
            applyFilters()
            setUpdatingJobs(false)
        }
    }, [availableJobs]);

    useEffect(() => {
        if(type) {
            switch (type) {
                case 'rate':
                    setOptions(rateOpts);
                    break;
                case 'distance':
                    setOptions(distanceOpts);
                    break;
                case 'sort':
                    setOptions(sortOptions);
                    break;
            }
        }
        if(refresh && !loadingJobs) {
            applyFilters()
            setRefresh(false)
        }
    }, [type, refresh, loadingJobs])

    useEffect(() => {
        if (exitReactState) {
            history.push('loading')
            exitReact('contact-support')
            setExitReactState(false)
        }
    }, [exitReactState])

    const applyFilters = (filterJobs?: JobProps) => {
        const sortedJobs = sorting(selectedSort, Array.isArray(filterJobs) ? filterJobs : availableJobs);
        const filteredByDistance = filterByDistance(distanceIdx, sortedJobs)
        const filteredByRate = filterByRate(hygienist.status || 0, rateIdx, filteredByDistance, rateOpts)
        if(favourite) {
            filteredByRate.length && setFilteredJobs([...filteredByRate.filter(job => job.isFavouriteDentist)])
        } else {
            setFilteredJobs(filteredByRate)
        }
        setType(null)
    }

    const dropFilters = () => {
        setRate(0);
        setDistance(0);
        setSelectedSort(0);
        setFavourite(false);
        setRefresh(true);
    }

    const unhideShifts = async () => {
        if (hidedJobs.length) {
            await TempStars.Api.unhideJobs();
            availableJobs.push(...hidedJobs);
            while(!!hidedJobs.length) {
                hidedJobs.pop()
            }
            applyFilters()
        }
    }

    const updateFavourite = async ({ dentistId, locationId, isFavouriteDentist }: JobProps) => {
        setUpdatingShifts(true)
        if (isFavouriteDentist) {
            await Api.removeFavouriteDentistByLocationId(locationId);
        } else {
            await Api.setFavouriteDentist(dentistId, locationId);
        }
        const shifts = await Api.getAvailableJobs(hygienist.type);

        const fs = shifts.filter((job: JobProps) => filteredJobs.find(fj => fj.id === job.id))
        applyFilters(fs)
        setUpdatingShifts(false);
    }

    return (
        <Page>
            <TopBarRedesigned
                title={'Available Temping Shifts'}
                onBack={() => {
                    history.push('loading')
                    exitReact('home')
                }}
            />
            <Container>
                {(date && withDate) ? <div className="subtitle">
                    {`Shifts for: ${date && formatDate(date, 'dddd, MMMM D, YYYY' )}`}
                </div> : null}
                <div className="favFilter">
                    <div className={`favButton ${favourite ? '' : 'underlined'}`} onClick={dropFilters}>All Shifts ({availableJobs.length})</div>
                    <div>
                        <div className={`favButton ${favourite ? 'underlined' : ''}`} onClick={() => {setFavourite(true); setRefresh(true)}}>
                            Shifts from favourite offices ({availableJobs.filter(job => job.isFavouriteDentist)?.length || 0})
                        </div>
                        {!!hidedJobs.length && <div className={`favButton`} style={{paddingTop: '1rem'}} onClick={unhideShifts}>Unhide Shifts ({hidedJobs.length})</div>}
                    </div>
                </div>
                <div className="filteringBox">
                    <div className="filterButton" onClick={() => setType(type === 'sort' ? null : 'sort')}>
                        <ReverseSVG width={18} color={'#134074'} />
                        Sort: {sortOptions[selectedSort]}
                    </div>
                    <div className="filterButton" onClick={() => setType(type === 'distance' ? null : 'distance')}>
                        Distance: {distanceOpts[distanceIdx]}
                    </div>
                    <div className="filterButton" onClick={() => setType(type === 'rate' ? null : 'rate')}>
                        Hourly Rate: {rateOpts[rateIdx]}
                    </div>
                </div>
                {type === 'sort' && <Dropdown
                    className={'muiFilter'}
                    options={options}
                    selected={selectedSort}
                    setSelected={setSelectedSort}
                    applyFilters={applyFilters}
                    clearFilter={() => {
                        setSelectedSort(0);
                        setRefresh(true);
                    }}
                    type={type}
                />}
                {type === 'distance' && <Dropdown
                    className={'muiFilter'}
                    options={distanceOpts}
                    selected={distanceIdx}
                    setSelected={setDistance}
                    applyFilters={applyFilters}
                    clearFilter={() => {
                        setDistance(0);
                        setRefresh(true);
                    }}
                    type={type}
                />}
                {type === 'rate' && <Dropdown
                    className={'muiFilter'}
                    options={rateOpts}
                    selected={rateIdx}
                    setSelected={setRate}
                    applyFilters={applyFilters}
                    clearFilter={() => {
                        setRate(0);
                        setRefresh(true);
                    }}
                    type={type}
                />}

                <Shifts
                    goToPage={goToPage}
                    availableJobs={filteredJobs}
                    errorGetJos={errorGetJos}
                    updateFavourite={updateFavourite}
                    updating={updatingShifts || loadingJobs}
                />
            </Container>
        <FixedLoader
            loading={updatingShifts || updatingJobs || loadingJobs}
        />
        </Page>)
}
